import "../patched/three.oc";
import { AxisAngle } from "../math/AxisAngle";
import "./Object3DExtensions";
import { OBB } from "three/examples/jsm/math/OBB";
import { v3 } from "../short3";
import { abs } from "../Maths3";
import {
  Box2,
  Box3,
  Color,
  Euler,
  Line3,
  Matrix3,
  Matrix4,
  Plane,
  Quaternion,
  Ray,
  Raycaster,
  Sphere,
  Vector2,
  Vector3,
  Vector4,
} from "three";

/**
 * Gibt die Drehung zurück, mit der man von einer Orientierung zu einer anderen gelangt.
 */
if (Quaternion.prototype.getRotationFromMeTo === undefined) {
  Quaternion.prototype.getRotationFromMeTo = function (b) {
    let a = this.clone();
    return a.invert().multiply(b);
  };
}

/**
 * Gibt den Winkel im Bogenmaß zwischen diesem und einer anderen Quaternion zurück.
 */
if (Quaternion.prototype.angleTo === undefined) {
  Quaternion.prototype.angleTo = function (otherQuaternion) {
    // https://math.stackexchange.com/questions/90081/quaternion-distance
    return Math.acos(2 * Math.pow(this.dot(otherQuaternion), 2) - 1);
  };
}

/**
 * Sind beide Quaternionen eine ähnliche Rotation?
 * garantiert true, wenn beide dieselbe Rotation darstellen. Basiert auf einer groben Einschätzung der Distanz, ist aber sehr schnell
 */
if (Quaternion.prototype.isCloseTo === undefined) {
  Quaternion.prototype.isCloseTo = function (otherQuaternion, epsilon = 0.01) {
    // https://math.stackexchange.com/questions/90081/quaternion-distance
    return 1 - Math.pow(this.dot(otherQuaternion), 2) < epsilon;
  };
}

/**
 * Gibt den Quaternion als AxisAngle Objekt zurück
 */
if (Quaternion.prototype.getAxisAngle === undefined) {
  Quaternion.prototype.getAxisAngle = function () {
    let angle = 2 * Math.acos(this.w);
    let s = Math.sqrt(1 - this.w * this.w);
    let axis = new Vector3(this.x, this.y, this.z);
    if (s > 0.001) {
      axis.multiplyScalar(1 / s);
    }
    return new AxisAngle(axis, angle);
  };
}

/**
 * Gibt die Rotationsachse des Quaternion zurück
 */
if (Quaternion.prototype.getAxis === undefined) {
  Quaternion.prototype.getAxis = function () {
    return new Vector3(this.x, this.y, this.z).normalize();
  };
}

/**
 * Berechnet das Perp-Dot-Produkt.
 * The perp dot product’s most useful property is that it gives us an easy way to compute the sine of the smaller angle between two vectors:
 * |v∗w|=|v||w|sin(α)
 * http://devmag.org.za/2009/08/12/vector-fundamentals/
 * http://cosmonautica.com/blog/2014/06/06/vectorguide/
 * taken from https://github.com/timprepscius/GeometricTools/blob/1623f4962bfaf3d5e4e42622b74664ce1b79ecf3/WildMagic5/LibMathematics/Rational/Wm5IVector2.cpp
 */
if (Vector2.prototype.dotPerp === undefined) {
  Vector2.prototype.dotPerp = function (v) {
    return this.x * v.y - this.y * v.x;
  };
}

/**
 * Interpretiert diesen Vektor als Size einer Box und prüft, ob der übergebene Vector (als Size) darin enthalten ist
 */
if (Vector3.prototype.containsSize === undefined) {
  Vector3.prototype.containsSize = function (b) {
    return this.x >= b.x && this.y >= b.y && this.z >= b.z;
  };
}

/**
 * gibt das Volumen eine Box3 zurück
 */
if (Box3.prototype.volume === undefined) {
  Box3.prototype.volume = function () {
    let size = this.getSize(new Vector3());
    return size.x * size.y * size.z;
  };
}

/**
 * gibt den Seiten-Mittelpunkt einer Box3-Seitenfläche zurück. side ist dabei "min" oder "max" und axis ist "x","y" oder "z".
 */
if (Box3.prototype.getFaceCenter === undefined) {
  Box3.prototype.getFaceCenter = function (side, axis) {
    const center = this.getCenter(new Vector3());
    center[axis] = this[side][axis];
    return center;
  };
}

/**
 * gibt die Ecken einer Box als Vector 3 zurück;
 * @returns {Array}
 */
if (Box3.prototype.getCorners === undefined) {
  Box3.prototype.getCorners = function () {
    const corners = [];
    ["min", "max"].forEach((m) => {
      corners.push(new Vector3().copy(this[m]));
      const other = m == "min" ? "max" : "min";
      ["x", "y", "z"].forEach((a) => {
        const v = new Vector3().copy(this[m]);
        v[a] = this[other][a];
        corners.push(v);
      });
    });
    return corners;
  };
}

/**
 * https://github.com/OC-Git/oc-base/wiki/Grid-Koordinaten
 */
if (Box3.prototype.grid === undefined) {
  Box3.prototype.grid = function (vectorOrX, y, z) {
    const v = typeof vectorOrX === "number" ? new Vector3(vectorOrX, y, z) : vectorOrX;
    return this.getSize(new Vector3()).multiply(v).add(this.min);
  };
}

if (Box2.prototype.grid === undefined) {
  Box2.prototype.grid = function (vectorOrX, y) {
    const v = typeof vectorOrX === "number" ? new Vector2(vectorOrX, y) : vectorOrX;
    return this.getSize(new Vector2()).multiply(v).add(this.min);
  };
}

if (OBB.prototype.grid === undefined) {
  OBB.prototype.grid = function (vectorOrX, y, z) {
    const v = typeof vectorOrX === "number" ? new Vector3(vectorOrX, y, z) : vectorOrX.clone();
    v.add(new Vector3(-0.5, -0.5, -0.5));
    v.multiply(this.getSize(new Vector3()));
    v.applyMatrix3(this.rotation);
    v.add(this.center);
    return v;
  };
}

/**
 * korrigiert diverse Bugs in OBB.applyMatrix4 - wenn die det der matrix negativ
 * ist (eine spiegellung enthält, z.b. scale.x=-1), dann würden die halfSize negative
 * Werte enthalten, was der Definition einer OBB widerspricht. In diesem Falle wäre auch die
 * Position falsch. Weiterhin scheint applyMatrix4 auch buggy zu sein,
 * wenn center nicht im Nullpunkt liegt.
 */
OBB.prototype.applyMatrix4 = function (matrix) {
  this.center.applyMatrix4(matrix);

  const rotationMatrix = new Matrix3().setFromMatrix4(matrix);
  this.rotation.premultiply(rotationMatrix);

  const scaleX = new Vector3().setFromMatrixColumn(matrix, 0).length();
  const scaleY = new Vector3().setFromMatrixColumn(matrix, 1).length();
  const scaleZ = new Vector3().setFromMatrixColumn(matrix, 2).length();

  this.halfSize.multiply(new Vector3(scaleX, scaleY, scaleZ));

  return this;
};

/**
 * gibt die Ecken einer OBB als Vector 3 zurück;
 * @returns {Array}
 */
if (OBB.prototype.getCorners === undefined) {
  OBB.prototype.getCorners = function () {
    return [
      new Vector3(0, 0, 0),
      new Vector3(0, 0, 1),
      new Vector3(0, 1, 0),
      new Vector3(0, 1, 1),
      new Vector3(1, 1, 0),
      new Vector3(1, 1, 1),
      new Vector3(1, 0, 0),
      new Vector3(1, 0, 1),
    ].map((v) => this.grid(v));
  };
}

if (OBB.prototype.toBox3 === undefined) {
  OBB.prototype.toBox3 = function () {
    return new Box3().setFromPoints(this.getCorners());
  };
}

if (OBB.prototype.expandByVector === undefined) {
  OBB.prototype.expandByVector = function (v) {
    this.halfSize.add(v.clone().multiplyScalar(0.5));
    return this;
  };
}

/**
 * Erzeugt eine neue OBB, die die Schnittmenge von obbA und obbB repräsentiert.
 * Gibt `null` zurück, falls keine Überschneidung vorhanden ist.
 * Die Schnittmenge ist gleich orientiert wie die größere OBB.
 */
if (OBB.prototype.intersect === undefined) {
  OBB.prototype.intersect = function (obb) {
    if (!this.intersectsOBB(obb)) {
      return null;
    }
    return booleanOBB([this, obb], (boxA, boxB) => boxA.intersect(boxB));
  };
}

/**
 * erweitert eine OBB so, dass eine andere OBB enthalten ist.
 * Die Union ist gleich orientiert wie die größere OBB.
 */
if (OBB.prototype.union === undefined) {
  OBB.prototype.union = function (obb) {
    return booleanOBB([this, obb], (boxA, boxB) => boxA.union(boxB));
  };
}

function booleanOBB(obbs, booleanFN) {
  const [obbB, obbA] = _.sortBy(obbs, (a) => a.halfSize.length());
  const matrix4 = obbA.getMatrix4();
  const inverseMatrix4 = new Matrix4().copy(matrix4).invert();
  const [boxA, boxB] = [obbA, obbB].map((obb) =>
    new Box3().setFromPoints(obb.getCorners().map((corner) => corner.applyMatrix4(inverseMatrix4))),
  );
  const boxBooleasn = booleanFN(boxA, boxB);
  if (boxBooleasn.isEmpty()) {
    return null;
  }
  const size = boxBooleasn.getSize(v3());
  if (size.toArray().some((x) => x < 0)) {
    debugger;
  }
  const center = boxBooleasn.getCenter(v3());
  const obbResult = new OBB();
  obbResult.halfSize.copy(size).multiplyScalar(0.5);
  obbResult.applyMatrix4(matrix4);
  obbResult.center.copy(center).applyMatrix4(matrix4);
  return obbResult;
}

/**
 * Liefert eine 4x4-Matrix aus center (Translation) und rotation (Matrix3).
 */
if (OBB.prototype.getMatrix4 === undefined) {
  OBB.prototype.getMatrix4 = function () {
    const mat4 = new Matrix4();
    const r = this.rotation.elements;
    const rot4 = new Matrix4().set(
      r[0],
      r[3],
      r[6],
      0,
      r[1],
      r[4],
      r[7],
      0,
      r[2],
      r[5],
      r[8],
      0,
      0,
      0,
      0,
      1,
    );
    mat4.multiply(rot4);
    const { x, y, z } = this.center;
    mat4.setPosition(x, y, z);
    return mat4;
  };
}

/**
 * stellt sicher, dass min<max ist, dass die Box also nie empty ist
 * @returns {Array}
 */
if (Box3.prototype.heal === undefined) {
  Box3.prototype.heal = function () {
    const a = this.min.clone();
    this.min.min(this.max);
    this.max.max(a);
    return this;
  };
}

/**
 * wendet ein Quaternion auf die Box an
 * @returns {Array}
 */
if (Box3.prototype.applyQuaternion === undefined) {
  Box3.prototype.applyQuaternion = function (quaternion) {
    const center = this.getCenter(new Vector3());
    this.translate(center.clone().multiplyScalar(-1));
    const min = this.min.clone().applyQuaternion(quaternion);
    const max = this.max.clone().applyQuaternion(quaternion);
    this.setFromPoints([min, max]);
    this.translate(center);
    return this;
  };
}

/**
 * stellt sicher, dass min<max ist, dass die Box also nie empty ist
 * @returns {Array}
 */
if (Box3.prototype.isZero === undefined) {
  const v0 = new Vector3();
  const epsilon = 0.0000001;
  Box3.prototype.isZero = function () {
    return this.min.manhattanDistanceTo(v0) < epsilon && this.max.manhattanDistanceTo(v0) < epsilon;
  };
}

/**
 * fehlt in threejs
 */
if (Vector4.prototype.distanceTo === undefined) {
  Vector4.prototype.distanceTo = function (v) {
    return Math.sqrt(this.distanceToSquared(v));
  };
}

/**
 * fehlt in threejs
 */
if (Vector4.prototype.distanceToSquared === undefined) {
  Vector4.prototype.distanceToSquared = function (v) {
    let dx = this.x - v.x,
      dy = this.y - v.y,
      dz = this.z - v.z,
      dw = this.w - v.w;
    return dx * dx + dy * dy + dz * dz + dw * dw;
  };
}

/**
 * fehlt in threejs
 */
if (Raycaster.prototype.clone === undefined) {
  Raycaster.prototype.clone = function () {
    const { ray, near, far } = this;
    return new Raycaster(ray.origin, ray.direction, near, far).copy(this);
  };
}

/**
 * fehlt in threejs
 */
if (Raycaster.prototype.copy === undefined) {
  Raycaster.prototype.copy = function (v) {
    this.ray.origin.copy(v.ray.origin);
    this.ray.direction.copy(v.ray.direction);
    this.near = v.near;
    this.far = v.far;
    return this;
  };
}

/**
 * gibt die Rotationskomponente einer Matrix als Quaternion zurück
 */
if (Matrix4.prototype.getRotation === undefined) {
  Matrix4.prototype.getRotation = function () {
    const rotation = new Quaternion();
    this.decompose(new Vector3(), rotation, new Vector3());
    return rotation;
  };
}

function ObservableVector3(
  onChangeCallback: (axis: "x" | "y" | "z", v: number) => void = undefined,
) {
  /** @ts-ignore */
  Object.defineProperties(this, {
    onChangeCallbacks: {
      value: onChangeCallback ? [onChangeCallback] : [],
      enumerable: false,
      writable: true,
    },
    _x: {
      value: 0,
      enumerable: false,
      writable: true,
    },
    _y: {
      value: 0,
      enumerable: false,
      writable: true,
    },
    _z: {
      value: 0,
      enumerable: false,
      writable: true,
    },
    x: {
      enumerable: true,
      get: function () {
        return this._x;
      },
      set: function (v) {
        if (v == null || Number.isNaN(v)) {
          throw new Error("attempt so set x = NaN");
        }
        this._x = v;
        for (const onChangeCallback of this.onChangeCallbacks) {
          onChangeCallback("x", v);
        }
      },
    },
    y: {
      enumerable: true,
      get: function () {
        return this._y;
      },
      set: function (v) {
        if (v == null || Number.isNaN(v)) {
          throw new Error("attempt so set y = NaN");
        }
        this._y = v;
        for (const onChangeCallback of this.onChangeCallbacks) {
          onChangeCallback("y", v);
        }
      },
    },
    z: {
      enumerable: true,
      get: function () {
        return this._z;
      },
      set: function (v) {
        if (v == null || Number.isNaN(v)) {
          throw new Error("attempt so set z = NaN");
        }
        this._z = v;
        for (const onChangeCallback of this.onChangeCallbacks) {
          onChangeCallback("z", v);
        }
      },
    },
  });
}

ObservableVector3.prototype = Object.create(Vector3.prototype);

/**
 * initWithObservation erzeugt eine neue, modifizierte Vector3 - Instanz,
 * welche eine Callback-Methode bei jeder Änderung aufruft. Ein mit dieser Methode erzeugter
 * Vector ist weniger performant als ein normaler Vector, da alle Zugriffe auf x,y,z intern über
 * Getter/Setter - Methoden laufen.
 * @example
 * const vector = THREE.Vector3.initWithObservation(()=>{
 *    console.log('I was changed');
 * });
 * vector.x=5; // => 'I was changed'
 */
Vector3.initWithObservation = function (onChangeCallback) {
  const o = new ObservableVector3();
  if (onChangeCallback) {
    o.onChangeCallbacks.push(onChangeCallback);
  }
  return o;
};

/**
 * Speichern / Laden diverser Grundobjekte als JSON Format
 */
Vector2.prototype.toJSON = function () {
  return { x: this.x, y: this.y };
};
Vector3.prototype.toJSON = function () {
  return { x: this.x, y: this.y, z: this.z };
};
Vector4.prototype.toJSON = function () {
  return { w: this.w, x: this.x, y: this.y, z: this.z };
};

/**
 * ACHTUNG! Quaternion.toJSON() wurde von THREE in der Zwischenzeit anders implementiert, als toArray() !
 * Um abwärtskompatibel zu bleiben, verwenden wir aber weiterhin unser ursprüngliches Format!
 * Das ist aber einer Fehlerquelle für THREE-Code oder auf THREE aufbauenden Code!
 */
declare module "three" {
  interface Quaternion {
    toJSON(): { w: number; x: number; y: number; z: number };
  }
}
// @ts-ignore
Quaternion.prototype.toJSON = function () {
  return { w: this.w, x: this.x, y: this.y, z: this.z };
};
Euler.prototype.toJSON = function () {
  return { x: this.x, y: this.y, z: this.z, order: this.order };
};
Color.prototype.toJSON = function () {
  return this.getHex();
};
Box2.prototype.toJSON = function () {
  return { min: this.min.toJSON(), max: this.max.toJSON() };
};
export interface Vector3Like {
  x: number;
  y: number;
  z: number;
}
/**
 * ACHTUNG! Box.toJSON() wurde von THREE in der Zwischenzeit anders implementiert, als toArray() !
 * Um abwärtskompatibel zu bleiben, verwenden wir aber weiterhin unser ursprüngliches Format!
 * Das ist aber einer Fehlerquelle für THREE-Code oder auf THREE aufbauenden Code!
 */
declare module "three" {
  interface Box {
    toJSON(): { min: Vector3Like; max: Vector3Like };
  }
}
// @ts-ignore
Box3.prototype.toJSON = function () {
  return { min: this.min.toJSON(), max: this.max.toJSON() };
};
Line3.prototype.toJSON = function () {
  return { start: this.start.toJSON(), end: this.end.toJSON() };
};
OBB.prototype.toJSON = function () {
  return {
    center: this.center.toJSON(),
    halfSize: this.halfSize.toJSON(),
    rotation: Array.from(this.rotation.elements) as number[],
  };
};
Plane.prototype.toJSON = function () {
  return { normal: this.normal.toJSON(), constant: this.constant };
};
Ray.prototype.toJSON = function () {
  return { origin: this.origin.toJSON(), direction: this.direction.toJSON() };
};
/**
 * ACHTUNG! Sphere.toJSON() wurde von THREE in der Zwischenzeit anders implementiert, als toArray() !
 * Um abwärtskompatibel zu bleiben, verwenden wir aber weiterhin unser ursprüngliches Format!
 * Das ist aber einer Fehlerquelle für THREE-Code oder auf THREE aufbauenden Code!
 */
declare module "three" {
  interface Sphere {
    toJSON(): { center: Vector3Like; radius: number };
  }
}
// @ts-ignore
Sphere.prototype.toJSON = function () {
  return { center: this.center.toJSON(), radius: this.radius };
};

declare module "three/examples/jsm/math/OBB" {
  interface OBB {
    toJSON(): {
      center: { x: number; y: number; z: number };
      halfSize: { x: number; y: number; z: number };
      rotation: number[];
    };
    fromJSON(json: ReturnType<OBB["toJSON"]>): this;
  }
  // @ts-ignore
  interface OBBStatic {
    fromJSON(json: ReturnType<OBB["toJSON"]> | null): OBB | null;
  }
}

Vector2.prototype.fromJSON = function (json) {
  this.set(json.x ?? 0, json.y ?? 0);
  return this;
};
Vector3.prototype.fromJSON = function (json) {
  this.set(json.x ?? 0, json.y ?? 0, json.z ?? 0);
  return this;
};
Vector4.prototype.fromJSON = function (json) {
  this.set(json.x ?? 0, json.y ?? 0, json.z ?? 0, json.w ?? 0);
  return this;
};
Quaternion.prototype.fromJSON = function (json) {
  this.set(json.x ?? 0, json.y ?? 0, json.z ?? 0, json.w ?? 0);
  return this;
};
Euler.prototype.fromJSON = function (json) {
  this.set(json.x ?? 0, json.y ?? 0, json.z ?? 0, json.order);
  return this;
};
Color.prototype.fromJSON = function (hex) {
  this.set(hex);
  return this;
};
Box2.prototype.fromJSON = function (json) {
  // leider serialisiert JSON Infinity als null
  const isNull = (v) => v?.x == null || v?.y == null;
  if (isNull(json.min) || isNull(json.max)) {
    this.makeEmpty();
  } else {
    this.set(Vector2.fromJSON(json.min), Vector2.fromJSON(json.max));
  }
  return this;
};
Box3.prototype.fromJSON = function (json) {
  // leider serialisiert JSON Infinity als null
  const isNull = (v) => v?.x == null || v?.y == null || v.z == null;
  if (isNull(json.min) || isNull(json.max)) {
    this.makeEmpty();
  } else {
    this.set(Vector3.fromJSON(json.min), Vector3.fromJSON(json.max));
  }
  return this;
};
Line3.prototype.fromJSON = function (json) {
  this.set(Vector3.fromJSON(json.start), Vector3.fromJSON(json.end));
  return this;
};
Plane.prototype.fromJSON = function (json) {
  this.set(Vector3.fromJSON(json.normal), json.constant);
  return this;
};
Ray.prototype.fromJSON = function (json) {
  this.set(Vector3.fromJSON(json.origin), Vector3.fromJSON(json.direction));
  return this;
};
Sphere.prototype.fromJSON = function (json) {
  this.set(Vector3.fromJSON(json.center), json.radius);
  return this;
};
OBB.prototype.fromJSON = function (json) {
  this.center.fromJSON(json.center);
  this.halfSize.fromJSON(json.halfSize);
  this.rotation.fromArray(json.rotation);
  return this;
};

Vector2.fromJSON = function (json) {
  return json == null ? null : new Vector2().fromJSON(json);
};
Vector3.fromJSON = function (json) {
  return json == null ? null : new Vector3().fromJSON(json);
};
Vector4.fromJSON = function (json) {
  return json == null ? null : new Vector4().fromJSON(json);
};
Quaternion.fromJSON = function (json) {
  return json == null ? null : new Quaternion().fromJSON(json);
};
Color.fromJSON = function (json) {
  return json == null ? null : new Color().fromJSON(json);
};
Box2.fromJSON = function (json) {
  return json == null ? null : new Box2().fromJSON(json);
};
Box3.fromJSON = function (json) {
  return json == null ? null : new Box3().fromJSON(json);
};
Line3.fromJSON = function (json) {
  return json == null ? null : new Line3().fromJSON(json);
};
Plane.fromJSON = function (json) {
  return json == null ? null : new Plane().fromJSON(json);
};
Ray.fromJSON = function (json) {
  return json == null ? null : new Ray().fromJSON(json);
};
Sphere.fromJSON = function (json) {
  return json == null ? null : new Sphere().fromJSON(json);
};
// @ts-ignore
OBB.fromJSON = function (json) {
  return json == null ? null : new OBB().fromJSON(json);
};

/**
 * vergleicht nur die Koordinaten auf Gleichheit bezüglich eines epsilons
 */
if (Vector2.prototype.isEqualEpsilon === undefined) {
  Vector2.prototype.isEqualEpsilon = function (other, epsilon = 0.000001) {
    return (
      other != null && Math.abs(other.x - this.x) < epsilon && Math.abs(other.y - this.y) < epsilon
    );
  };
}
if (Vector3.prototype.isEqualEpsilon === undefined) {
  Vector3.prototype.isEqualEpsilon = function (other, epsilon = 0.000001) {
    return (
      other != null &&
      Math.abs(other.x - this.x) < epsilon &&
      Math.abs(other.y - this.y) < epsilon &&
      Math.abs(other.z - this.z) < epsilon
    );
  };
}
if (Vector4.prototype.isEqualEpsilon === undefined) {
  Vector4.prototype.isEqualEpsilon = function (other, epsilon = 0.000001) {
    return (
      other != null &&
      Math.abs(other.x - this.x) < epsilon &&
      Math.abs(other.y - this.y) < epsilon &&
      Math.abs(other.z - this.z) < epsilon &&
      Math.abs(other.w - this.w) < epsilon
    );
  };
}
if (Quaternion.prototype.isEqualEpsilon === undefined) {
  Quaternion.prototype.isEqualEpsilon = function (other, epsilon = 0.000001) {
    return (
      other != null &&
      Math.abs(other.x - this.x) < epsilon &&
      Math.abs(other.y - this.y) < epsilon &&
      Math.abs(other.z - this.z) < epsilon &&
      Math.abs(other.w - this.w) < epsilon
    );
  };
}
if (Matrix3.prototype.isEqualEpsilon === undefined) {
  Matrix3.prototype.isEqualEpsilon = function (other, epsilon = 0.000001) {
    if (other == null || other.elements == null || other.elements.length != this.elements.length) {
      return false;
    }
    for (let i = 0; i < this.elements.length; i++) {
      if (Math.abs(other.elements[i] - this.elements[i]) < epsilon) {
        return false;
      }
    }
    return true;
  };
}
if (Matrix4.prototype.isEqualEpsilon === undefined) {
  Matrix4.prototype.isEqualEpsilon = function (other, epsilon = 0.000001) {
    if (other == null || other.elements == null || other.elements.length != this.elements.length) {
      return false;
    }
    for (let i = 0; i < this.elements.length; i++) {
      if (Math.abs(other.elements[i] - this.elements[i]) < epsilon) {
        return false;
      }
    }
    return true;
  };
}
if (Box3.prototype.isEqualEpsilon === undefined) {
  Box3.prototype.isEqualEpsilon = function (other, epsilon = 0.000001) {
    return (
      other != null &&
      this.min.isEqualEpsilon(other.min, epsilon) &&
      this.max.isEqualEpsilon(other.max, epsilon)
    );
  };
}
if (Box2.prototype.isEqualEpsilon === undefined) {
  Box2.prototype.isEqualEpsilon = function (other, epsilon = 0.000001) {
    return (
      other != null &&
      this.min.isEqualEpsilon(other.min, epsilon) &&
      this.max.isEqualEpsilon(other.max, epsilon)
    );
  };
}

/**
 * Source-Kompatibilität zu < r108
 */
if (Quaternion.prototype.onChange === undefined) {
  Quaternion.prototype.onChange = function (callback: () => void) {
    return this._onChange(callback);
  };
}
